<app-spinner [isLoading]="isUpdating$ | async" [isFixedPosition]="false">
  @if ({
    personInfo: selectPersonalInfo$ | async,
    sameAsCurrentAddress: sameAsCurrentAddress$ | async,
    legalAddress: legalAddress$ | async,
    address: address$ | async,
    locationLevelCount: locationLevelCount$ | async,
    locationRequiredLevelCount: locationRequiredLevelCount$ | async,
    locationLastHiddenLevelCount: locationLastHiddenLevelCount$ | async,
    firstLevelParentLocationId: locationFirstLevelId$ | async,
    moderationLastChangeDate: moderationLastChangeDate$ | async,
    genderList: genderList$ | async,
    days: days$ | async,
    monthList: monthList$ | async,
    yearListBirth: yearListBirth$ | async,
    drivingLicenseList: drivingLicenseList$ | async,
    vehicleTypes: vehicleTypes$ | async
  }; as data) {
    <form [formGroup]="editForm" #ngForm="ngForm" [appConnectForm]="data.personInfo" class="personal-info-edit">
      <div class="personal-info-edit__header">
        <h2 class="personal-info-edit__title">{{ 'label.PersonalInformation' | translate }}</h2>
        <button type="button" class="personal-info-edit__close" [mat-dialog-close]="true" [attr.aria-label]="'label.Close' | translate">
          <svg-icon src="/assets/icons/dialog-close.svg"></svg-icon>
        </button>
      </div>

      <div class="personal-info-edit__body">
        <div class="fields">
          <div class="fields__row fields__row--halves">
            <div>
              <hrra-floating-field>
                <input pInputText id="personal-info-name" type="text" formControlName="name" name="name">
                <label for="personal-info-name">{{ 'label.Name' | translate }}</label>
              </hrra-floating-field>
              @if (editForm.controls['name'].hasError('required') && (editForm.controls['name'].touched || ngForm.submitted)) {
                <mat-error class="validation-message">{{ 'message.MandatoryField' | translate }}</mat-error>
              }
              @if (editForm.controls['name'].hasError('pattern') && (editForm.controls['name'].touched || ngForm.submitted)) {
                <mat-error class="validation-message">{{ 'message.IncorrectFormat' | translate }}</mat-error>
              }
            </div>

            <div>
              <hrra-floating-field>
                <input pInputText id="personal-info-surname" type="text" formControlName="surname" name="surname">
                <label for="personal-info-surname">{{ 'label.Surname' | translate }}</label>
              </hrra-floating-field>
              @if (editForm.controls['surname'].hasError('required') && (editForm.controls['surname'].touched || ngForm.submitted)) {
                <mat-error class="validation-message">{{ 'message.MandatoryField' | translate }}</mat-error>
              }
              @if (editForm.controls['surname'].hasError('pattern') && (editForm.controls['surname'].touched || ngForm.submitted)) {
                <mat-error class="validation-message">{{ 'message.IncorrectFormat' | translate }}</mat-error>
              }
            </div>
          </div>

          <div>
            <hrra-floating-field>
              <input pInputText id="personal-info-email" type="email" formControlName="email" name="email">
              <label for="personal-info-email">{{ 'label.Email' | translate }}</label>
            </hrra-floating-field>
            <p class="hint">
              {{ 'text.ChangeContactEmailText' | translate }}
              <a (click)="goToSettings()" class="hint__link">{{ 'text.SettingsLabel' | translate }}</a>
            </p>
            @if (editForm.controls['email'].hasError('required') && (editForm.controls['email'].touched || ngForm.submitted)) {
              <mat-error class="validation-message">{{ 'message.MandatoryField' | translate }}</mat-error>
            }
            @if (editForm.controls['email'].hasError('pattern') && (editForm.controls['email'].touched || ngForm.submitted)) {
              <mat-error class="validation-message">{{ 'message.IncorrectFormat' | translate }}</mat-error>
            }
          </div>

          <div class="fields__row fields__row--halves">
            <div>
              <hrra-floating-field>
                <input pInputText id="personal-info-phone" maxlength="30" type="text" formControlName="phone" name="phone">
                <label for="personal-info-phone">{{ 'label.Phone' | translate }} 1</label>
              </hrra-floating-field>
              @if (editForm.controls['phone'].hasError('required') && (editForm.controls['phone'].touched || ngForm.submitted)) {
                <mat-error class="validation-message">{{ 'message.MandatoryField' | translate }}</mat-error>
              }
              @if (editForm.controls['phone'].hasError('pattern') && (editForm.controls['phone'].touched || ngForm.submitted)) {
                <mat-error class="validation-message">{{ 'message.IncorrectFormat' | translate }}</mat-error>
              }
            </div>

            <div>
              <hrra-floating-field>
                <input pInputText id="personal-info-phone2" maxlength="30" type="text" formControlName="phone2" name="phone2">
                <label for="personal-info-phone2">{{ 'label.Phone' | translate }} 2</label>
              </hrra-floating-field>
              @if (editForm.controls['phone2'].invalid) {
                <mat-error class="validation-message">{{ 'message.IncorrectFormat' | translate }}</mat-error>
              }
            </div>
          </div>

          <hrra-floating-field>
            <p-select inputId="personal-info-gender" formControlName="gender" [options]="data.genderList" optionLabel="name" optionValue="value" appDialogPanel></p-select>
            <label for="personal-info-gender">{{ 'label.Gender' | translate }}</label>
          </hrra-floating-field>

          <div class="fields__row fields__row--birth-date">
            <hrra-floating-field>
              <p-select inputId="personal-info-birth-day" formControlName="birthDay" [options]="data.days" appDialogPanel></p-select>
              <label for="personal-info-birth-day">{{ 'label.DayLabel' | translate }}</label>
            </hrra-floating-field>

            <hrra-floating-field>
              <p-select inputId="personal-info-birth-month" formControlName="birthMonth" [options]="data.monthList" optionLabel="name" optionValue="value" appDialogPanel (onChange)="onSelectMonth()"></p-select>
              <label for="personal-info-birth-month">{{ 'label.MonthLabel' | translate }}</label>
            </hrra-floating-field>

            <hrra-floating-field>
              <p-select inputId="personal-info-birth-year" formControlName="birthYear" [options]="data.yearListBirth" optionLabel="name" optionValue="value" appDialogPanel (onChange)="onSelectYear()"></p-select>
              <label for="personal-info-birth-year">{{ 'label.YearLabel' | translate }}</label>
            </hrra-floating-field>
          </div>

          <div class="location">
            <hrra-location-picker
              formControlName="extendedLocationPath"
              [locationLevelCount]="data.locationLevelCount"
              [firstLevelParentLocationId]="data.firstLevelParentLocationId"
              [requiredLevelCount]="data.locationRequiredLevelCount"
              [lastHiddenLevelCount]="data.locationLastHiddenLevelCount">
            </hrra-location-picker>
            <span class="location__message">{{ 'text.JobseekerExtendedLocationEditText' | translate }}</span>
          </div>

          <div>
            <hrra-floating-field>
              <input pInputText id="personal-info-address" (keyup)="updateJobseekerAddress($event)" type="text" formControlName="address">
              <label for="personal-info-address">{{ 'label.Address' | translate }}</label>
            </hrra-floating-field>
            @if (editForm.controls['address'].invalid) {
              <mat-error class="validation-message">{{ 'message.IncorrectFormat' | translate }}</mat-error>
            }
          </div>

          <div class="legal-address">
            <label class="checkbox">
              <input #sameAsCurrentAddressCheckbox type="checkbox" class="checkbox__control" [checked]="data.sameAsCurrentAddress" (change)="updateSameAsCurrentAddress(sameAsCurrentAddressCheckbox.checked)">
              <span class="checkbox__label">{{ 'label.SameAsCurrentAddress' | translate }}</span>
            </label>

            <hrra-floating-field>
              <input pInputText id="personal-info-legal-address" type="text" [value]="data.legalAddress ?? ''" (keyup)="updateJobseekerLegalAddress($event)" [attr.disabled]="data?.sameAsCurrentAddress ? '' : null">
              <label for="personal-info-legal-address">{{ 'label.LegalAddress' | translate }}</label>
            </hrra-floating-field>
          </div>

          <div class="fields__row fields__row--vehicle">
            <hrra-floating-field class="driving-licence">
              <p-multiselect inputId="personal-info-driving-licence" formControlName="drivingLicenses" [options]="data.drivingLicenseList" optionLabel="name" optionValue="value" [showHeader]="false" appDialogPanel></p-multiselect>
              <label for="personal-info-driving-licence">{{ 'label.DrivingLicence' | translate }}</label>
            </hrra-floating-field>

            <div class="own-vehicle">
              <span class="own-vehicle__label">{{ 'label.OwnTransport' | translate }}</span>
              <label class="radio">
                <input type="radio" class="radio__control" formControlName="ownVehicle" [value]="true" (change)="onChangeOwnVehicle()">
                <span class="radio__label">{{ 'label.Yes' | translate }}</span>
              </label>
              <label class="radio">
                <input type="radio" class="radio__control" formControlName="ownVehicle" [value]="false" (change)="onChangeOwnVehicle()">
                <span class="radio__label">{{ 'label.No' | translate }}</span>
              </label>
            </div>
          </div>

          <div>
            <hrra-floating-field>
              <p-multiselect inputId="personal-info-vehicle-type" formControlName="jobseekerVehicles" [options]="data.vehicleTypes" optionLabel="name" optionValue="value" [showHeader]="false" appDialogPanel></p-multiselect>
              <label for="personal-info-vehicle-type">{{ 'label.TransportType' | translate }}</label>
            </hrra-floating-field>
            @if (editForm.hasError('fillVehicleType') && (editForm.controls['jobseekerVehicles'].touched || ngForm.submitted)) {
              <mat-error class="validation-message">{{ 'message.MandatoryField' | translate }}</mat-error>
            }
          </div>
        </div>

        <div class="actions">
          <button type="submit" class="actions__button actions__button--save" [disabled]="!ngForm.valid" (click)="onSave(data.sameAsCurrentAddress, data.legalAddress, data.moderationLastChangeDate)">{{ 'label.Save' | translate }}</button>
          <button type="button" class="actions__button actions__button--cancel" [mat-dialog-close]="true">{{ 'label.Cancel' | translate }}</button>
        </div>
      </div>
    </form>
  }
</app-spinner>
